import React, { useEffect, useMemo, useState } from "react";
import { Form, message } from "antd";
import { useTranslation } from "react-i18next";
import CustomModal from "components/Common/CustomModal";
import CommonSelectLite from "components/Common/CommonSelectLite";
import { ClassLevel } from "types/classLevelTypes";
import { getBranchLabelFromCode, getSubjectLabelFromCode } from "utils/schoolUtils";

export interface AddSubjectCoefficientPayload {
  classLevelId: string;
  subjectId: string;
  coefficient: number;
  branchId?: string | null;
}

interface AddEditSubjectCoefficientModalProps {
  isOpen: boolean;
  onClose: () => void;
  classLevelId: string;

  /** Current class level with its subjectCoefficient list */
  currentClassLevel?: ClassLevel;

  /** Called on submit; should throw to show error */
  onSave: (payload: AddSubjectCoefficientPayload) => Promise<void>;
}

const AddEditSubjectCoefficientModal: React.FC<AddEditSubjectCoefficientModalProps> = ({
  isOpen,
  onClose,
  classLevelId,
  currentClassLevel,
  onSave,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const scList = currentClassLevel?.subjectCoefficient || [];

  // ---------- Derive branches from currentClassLevel ----------
  const branches = useMemo(
    () =>
      Array.from(
        new Map(
          scList
            .filter((sc: any) => sc.branch && sc.branch.name)
            .map((sc: any) => [sc.branch.id, { id: sc.branch.id, name: sc.branch.name , labelKey: sc.branch.labelKey }])
        ).values()
      ),
    [scList]
  );

  const hasBranches = branches.length > 0;

  const branchOptions = useMemo(
    () => branches.map((b) => ({ value: b.id, label:  getBranchLabelFromCode(b.labelKey, t) })),
    [branches]
  );

  // ---------- Watch selected branch ----------
  const watchedBranch = Form.useWatch("branchId", form);
  const selectedBranchId: string | null = watchedBranch?.value ?? watchedBranch ?? null;

  // ---------- Subjects already attached (NOT optional) in current scope ----------
  // If branches exist → only block subjects already added in THIS branch.
  // If no branches → block subjects already added in BASE (branch == null).
  const usedSubjectIdsInScope = useMemo(() => {
    const set = new Set<string>();
    for (const it of scList) {
      if (it.optional === true) continue; // only count attached/required ones
      const isSameScope = hasBranches
        ? it.branch?.id === selectedBranchId
        : !it.branch; // base scope
      if (isSameScope && it.subject?.id) set.add(it.subject.id);
    }
    return set;
  }, [scList, hasBranches, selectedBranchId]);

  // ---------- Optional subjects available in current scope (source of truth) ----------
  const optionalSubjectsInScope = useMemo(() => {
    return scList
      .filter((it: any) => it.optional === true)
      .filter((it: any) => {
        const scopeMatch = hasBranches ? it.branch?.id === selectedBranchId : !it.branch;
        return scopeMatch;
      })
      .map((it: any) => ({
        value: it.subject.id,
        label: it.subject.name,
        branchId: it.branch?.id ?? null,
        code: it.subject.code,
      }));
  }, [scList, hasBranches, selectedBranchId]);

  // ---------- Final subject options (exclude those already attached in scope) ----------
  const subjectOptions = useMemo(() => {
    if (hasBranches && !selectedBranchId) return []; // gate by branch selection
    return optionalSubjectsInScope.filter((s) => !usedSubjectIdsInScope.has(s.value));
  }, [optionalSubjectsInScope, usedSubjectIdsInScope, hasBranches, selectedBranchId]);

  useEffect(() => {
    if (isOpen) form.resetFields();
  }, [isOpen, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const finalBranchId: string | null =
        (values.branchId?.value ?? values.branchId ?? null) || null;

      if (hasBranches && !finalBranchId) {
        message.warning(t("validation.please_select_branch"));
        return;
      }

      const subjectId: string = values.subjectId?.value ?? values.subjectId;
      if (!subjectId) {
        message.warning(t("validation.please_select_subject"));
        return;
      }

      // Default coefficient: if the same subject exists anywhere in this level, reuse its coefficient; else 1
      const coefficient =
        currentClassLevel?.subjectCoefficient?.find((elm: any) => elm.subject.id === subjectId)?.coefficient ?? 1;

      const payload: AddSubjectCoefficientPayload = {
        classLevelId,
        subjectId,
        coefficient,
        branchId: hasBranches ? finalBranchId : null,
      };

      setSubmitting(true);
      await onSave(payload);
      message.success(t("subject_coefficient_added_successfully"));
      onClose();
    } catch {
      // antd shows validation msgs
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CustomModal
      title={t("add_subject")}
      isOpen={isOpen}
      onClose={onClose}
      onSave={handleSave}
      loading={submitting}
      saveText={t("add")}
    >
      <Form layout="vertical" form={form}>
        {hasBranches && (
          <Form.Item
            label={t("branch")}
            name="branchId"
            rules={[{ required: true, message: t("validation.please_select_branch") }]}
          >
            <CommonSelectLite
              className="w-100"
              options={branchOptions}
              optionValueKey="value"
              optionLabelKey="label"
              placeholder={t("select_branch")}
            />
          </Form.Item>
        )}

        <Form.Item
          label={t("subject")}
          name="subjectId"
          rules={[{ required: true, message: t("validation.please_select_subject") }]}
        >
          <CommonSelectLite
            className="w-100"
            options={subjectOptions.map((s) => ({
              value: s.value,
              label: getSubjectLabelFromCode(s.code, t),
            }))}
            optionValueKey="value"
            optionLabelKey="label"
            placeholder={
              hasBranches && !selectedBranchId
                ? t("select_branch_first")
                : t("select_subject")
            }
            disabled={hasBranches && !selectedBranchId}
          />
        </Form.Item>
      </Form>
    </CustomModal>
  );
};

export default AddEditSubjectCoefficientModal;
